import React, { useMemo } from 'react';

import { ContextMenu, type MenuItem } from 'components/common/ContextMenu';
import type { ActionContract } from 'model/actions';
import { groupActionsByCategory } from 'model/actions';
import { INTERNAL_ACTION_IDS, isForEachAction } from 'model/specialActions';
import type { WorkflowGraph } from 'model/xyflowMapping';

export interface ContextMenuState {
  x: number;
  y: number;
  nodeId: string | null;
  edgeId: string | null;
}

interface OwnProps {
  /** Coordinates + which graph element the user right-clicked (null = pane). */
  contextMenu: ContextMenuState | null;
  graph: WorkflowGraph;
  availableActions: ActionContract[];
  onClose: () => void;
  onDuplicateNode: (nodeId: string) => void;
  onDeleteNode: (nodeId: string) => void;
  onDeleteEdge: (edgeId: string) => void;
  onAddNodeAtPosition: (actionId: string, x: number, y: number, parentStepId?: string) => void;
}

/**
 * Decides what menu items to show based on whether the user right-clicked
 * a node, an edge, or empty pane — then renders `<ContextMenu>`. Pure:
 * the parent owns the `contextMenu` state and the operation handlers; we
 * just route the right items into the right shape.
 */
const EditorContextMenuController = ({
  contextMenu,
  graph,
  availableActions,
  onClose,
  onDuplicateNode,
  onDeleteNode,
  onDeleteEdge,
  onAddNodeAtPosition,
}: OwnProps) => {
  const items = useMemo<MenuItem[]>(() => {
    if (!contextMenu) return [];

    const buildGroupedAddItems = (parentStepId?: string): MenuItem[] => {
      const nonInternal = availableActions.filter((a) => !INTERNAL_ACTION_IDS.has(a.actionId));
      const grouped = groupActionsByCategory(nonInternal);
      const result: MenuItem[] = [];
      grouped.forEach((groupActions, category) => {
        result.push({ type: 'header', label: category });
        groupActions.forEach((action) => {
          result.push({
            label: action.displayName,
            onClick: () =>
              onAddNodeAtPosition(action.actionId, contextMenu.x, contextMenu.y, parentStepId),
          });
        });
      });
      return result;
    };

    if (contextMenu.nodeId) {
      const ctxNode = graph.nodes.find((n) => n.id === contextMenu.nodeId);
      const aid = ctxNode?.data.actionId ?? '';
      if (isForEachAction(aid)) {
        return buildGroupedAddItems(contextMenu.nodeId);
      }

      const isProtected = INTERNAL_ACTION_IDS.has(aid);
      const { nodeId } = contextMenu;
      return [
        {
          label: 'Duplicate',
          onClick: () => onDuplicateNode(nodeId),
          disabled: isProtected,
        },
        {
          label: 'Delete',
          onClick: () => onDeleteNode(nodeId),
          danger: true,
          disabled: isProtected,
        },
      ];
    }

    if (contextMenu.edgeId) {
      const { edgeId } = contextMenu;
      return [
        {
          label: 'Delete connection',
          onClick: () => onDeleteEdge(edgeId),
          danger: true,
        },
      ];
    }

    return buildGroupedAddItems();
  }, [
    contextMenu,
    graph.nodes,
    availableActions,
    onAddNodeAtPosition,
    onDuplicateNode,
    onDeleteNode,
    onDeleteEdge,
  ]);

  if (!contextMenu) return null;
  return <ContextMenu x={contextMenu.x} y={contextMenu.y} items={items} onClose={onClose} />;
};

export default React.memo(EditorContextMenuController);
